"use client";

import { useMemo, useRef, useState } from "react";
import Link from "next/link";
import { usePostHog } from "posthog-js/react";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { ArrowRight, ArrowUpRight, TrendingDown, Info } from "@/components/icons";
import { cn } from "@/lib/utils";
import { SectionHeader } from "@/components/ui";
import {
  PricingSlider,
  sliderThumbStyles,
} from "@/components/sections/products/shared/pricing-slider";
import { buildGbTiers } from "@/lib/pricing-utils";
import { useProductsInfo } from "@/lib/products-info";
import { REGISTER_URL, ROUTES } from "@/lib/constants";
import {
  DATAIMPULSE_MOBILE_URL,
  DATAIMPULSE_RESIDENTIAL_URL,
  DATAIMPULSE_TARGETING_DOCS_URL,
  DATAIMPULSE_TARGETING_MULTIPLIER,
  PRICING_VERIFIED_DATE,
  PROXIDIZE_MOBILE_FALLBACK_TIERS,
  PROXIDIZE_RESIDENTIAL_FALLBACK_TIERS,
  dataImpulseMonthlyCost,
  type ProxyProduct,
} from "./pricing-data";

const MotionDiv = motion.div;

const currency = (n: number) =>
  n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: n % 1 === 0 ? 0 : 2,
  });

const PRODUCT_META: Record<
  ProxyProduct,
  { label: string; dataimpulseUrl: string; pricingRoute: string }
> = {
  mobile: {
    label: "Mobile proxies",
    dataimpulseUrl: DATAIMPULSE_MOBILE_URL,
    pricingRoute: ROUTES.pricingMobileProxies,
  },
  residential: {
    label: "Residential proxies",
    dataimpulseUrl: DATAIMPULSE_RESIDENTIAL_URL,
    pricingRoute: ROUTES.pricingResidentialProxies,
  },
};

export default function DataimpulseCostCalculator() {
  const prefersReducedMotion = useReducedMotion();
  const posthog = usePostHog();
  const products = useProductsInfo();
  const hasTracked = useRef(false);

  const [product, setProduct] = useState<ProxyProduct>("mobile");
  const [granularTargeting, setGranularTargeting] = useState(false);

  // Live Proxidize prices when the pricing API responds; honest static
  // fallbacks (the same ones the pricing pages use) when it doesn't.
  const mobileTiers = useMemo(
    () => buildGbTiers(products?.["Monthly Per GB Mobile"] ?? [], PROXIDIZE_MOBILE_FALLBACK_TIERS),
    [products]
  );
  const residentialTiers = useMemo(
    () =>
      buildGbTiers(
        products?.["Monthly Per GB Residential"] ?? [],
        PROXIDIZE_RESIDENTIAL_FALLBACK_TIERS
      ),
    [products]
  );

  const tiers = product === "mobile" ? mobileTiers : residentialTiers;
  const steps = useMemo(() => tiers.map((t) => t.gb), [tiers]);

  const [gb, setGb] = useState(100);
  const selected =
    tiers.find((t) => t.gb === gb) ??
    tiers.reduce((best, t) => (Math.abs(t.gb - gb) < Math.abs(best.gb - gb) ? t : best), tiers[0]);
  const activeGb = selected.gb;

  // The multiplier applies to actual bytes transferred at the applicable package rate, not to a
  // separate "targeting package" or the account's base minimum purchase — see
  // DATAIMPULSE_TARGETING_MULTIPLIER for the source.
  const dataimpulse = dataImpulseMonthlyCost(product, activeGb);
  const dataimpulseTargetingCost = dataimpulse.cost * DATAIMPULSE_TARGETING_MULTIPLIER;
  const proxidizeCost = selected.totalPrice;
  const savings = dataimpulse.cost - proxidizeCost;
  const savingsPct = Math.round(
    (Math.abs(savings) / Math.max(dataimpulse.cost, proxidizeCost)) * 100
  );
  const meta = PRODUCT_META[product];

  function track() {
    if (!hasTracked.current) {
      hasTracked.current = true;
      posthog?.capture("alternatives_calculator_used", { competitor: "dataimpulse" });
    }
  }

  function handleChange(value: number) {
    setGb(value);
    track();
  }

  function handleProductSwitch(next: ProxyProduct) {
    setProduct(next);
    track();
    posthog?.capture("alternatives_calculator_product_switched", {
      competitor: "dataimpulse",
      product: next,
    });
  }

  function handleTargetingToggle() {
    const next = !granularTargeting;
    setGranularTargeting(next);
    track();
    posthog?.capture("alternatives_calculator_targeting_toggled", {
      competitor: "dataimpulse",
      product,
      enabled: next,
    });
  }

  return (
    <section
      id="cost-calculator"
      className="py-section border-border scroll-mt-24 border-b"
      aria-labelledby="calculator-heading"
    >
      <style>{sliderThumbStyles}</style>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <MotionDiv
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 24 }}
          whileInView={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.55 }}
          className="mb-14 max-w-2xl"
        >
          <SectionHeader
            eyebrow="Cost Calculator"
            id="calculator-heading"
            heading="What would your monthly proxy bill be?"
            body="Pick a product, drag the slider to your monthly data usage. Proxidize prices come from our live pricing; DataImpulse is priced at its published pay-as-you-go rate for that volume."
          />
        </MotionDiv>

        <MotionDiv
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 24 }}
          whileInView={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.55, delay: 0.08 }}
          className="bg-card card-wash card-hover border-border relative overflow-hidden rounded-2xl border p-6 sm:p-8"
        >
          <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-muted-foreground text-sm">Monthly data usage</p>
              <p className="text-foreground text-3xl font-bold tracking-tight sm:text-4xl">
                {activeGb.toLocaleString("en-US")} GB
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <label className="flex cursor-pointer items-center gap-2.5 text-sm">
                <span className="text-foreground font-medium whitespace-nowrap">
                  Add city or ASN targeting
                </span>
                <span
                  role="switch"
                  aria-checked={granularTargeting}
                  className={cn(
                    "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors",
                    granularTargeting ? "bg-foreground" : "bg-border"
                  )}
                >
                  <input
                    type="checkbox"
                    checked={granularTargeting}
                    onChange={handleTargetingToggle}
                    className="sr-only"
                  />
                  <span
                    className={cn(
                      "bg-background inline-block size-3.5 rounded-full transition-transform",
                      granularTargeting ? "translate-x-[18px]" : "translate-x-1"
                    )}
                    aria-hidden="true"
                  />
                </span>
              </label>

              <div
                role="group"
                aria-label="Proxy product"
                className="border-border inline-flex rounded-lg border p-1"
              >
                {(Object.keys(PRODUCT_META) as ProxyProduct[]).map((key) => (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={product === key}
                    onClick={() => handleProductSwitch(key)}
                    className={cn(
                      "cursor-pointer rounded-md px-4 py-1.5 text-sm font-medium transition-colors",
                      product === key
                        ? "bg-foreground text-background"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    {PRODUCT_META[key].label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <PricingSlider
            min={steps[0]}
            max={steps[steps.length - 1]}
            value={activeGb}
            onChange={handleChange}
            steps={steps}
            aria-label={`Monthly ${meta.label.toLowerCase()} data usage in gigabytes`}
          />

          <div className="mt-10 grid gap-5 sm:grid-cols-2">
            <div
              className="rounded-xl border p-6"
              style={{
                borderColor: "color-mix(in srgb, var(--brand-green) 35%, transparent)",
                backgroundColor: "color-mix(in srgb, var(--brand-green) 5%, transparent)",
              }}
            >
              <p className="text-foreground text-sm font-semibold">Proxidize</p>
              <p className="text-foreground mt-2 text-3xl font-bold tracking-tight sm:text-4xl">
                {currency(proxidizeCost)}
                <span className="text-muted-foreground text-base font-normal">/mo</span>
              </p>
              <p className="text-muted-foreground mt-1 text-sm">
                {currency(selected.monthlyPPG)}/GB · monthly plan
                {granularTargeting ? " · targeting included, no change" : ""}
              </p>
            </div>

            <div className="border-border rounded-xl border p-6">
              <p className="text-foreground text-sm font-semibold">DataImpulse</p>
              <p className="text-foreground mt-2 text-3xl font-bold tracking-tight sm:text-4xl">
                {currency(granularTargeting ? dataimpulseTargetingCost : dataimpulse.cost)}
                <span className="text-muted-foreground text-base font-normal">/mo</span>
              </p>
              <p className="text-muted-foreground mt-1 text-sm">
                {granularTargeting
                  ? `${dataimpulse.tierLabel} × ${DATAIMPULSE_TARGETING_MULTIPLIER} for city/ASN selection`
                  : dataimpulse.tierLabel}
              </p>
            </div>
          </div>

          {granularTargeting && (
            <p className="text-muted-foreground mt-3 text-xs">
              DataImpulse&rsquo;s targeting documentation states that traffic routed through a
              state, city, ZIP, or ASN selection is billed at double the standard rate — applied to
              the GB you actually use, not to your account&rsquo;s base package or minimum purchase.
            </p>
          )}

          <div aria-live="polite">
            {granularTargeting ? (
              <p className="text-foreground mt-6 flex items-center gap-2 text-sm font-medium">
                <Info size={16} style={{ color: "var(--yellow-400)" }} aria-hidden="true" />
                Proxidize&rsquo;s {currency(proxidizeCost)}/month already includes this targeting,
                no extra charge. DataImpulse would charge {currency(dataimpulseTargetingCost)}/month
                for the same targeting at this volume.
              </p>
            ) : savings > 1 ? (
              <p className="text-foreground mt-6 flex items-center gap-2 text-sm font-medium">
                <TrendingDown
                  size={16}
                  style={{ color: "var(--brand-green)" }}
                  aria-hidden="true"
                />
                DataImpulse would cost {currency(savings)}/month more ({savingsPct}%) than Proxidize
                at this volume.
              </p>
            ) : savings < -1 ? (
              <p className="text-muted-foreground mt-6 text-sm font-medium">
                At this volume, DataImpulse&rsquo;s published rate is {currency(-savings)}/month
                cheaper ({savingsPct}%) than Proxidize.
              </p>
            ) : (
              <p className="text-muted-foreground mt-6 text-sm font-medium">
                Both providers charge the same rate at this volume — the cost is a tie.
              </p>
            )}
          </div>

          <p className="text-muted-foreground mt-4 text-xs leading-relaxed">
            DataImpulse&rsquo;s pay-as-you-go rate is $2/GB mobile and $1/GB residential under 1,000
            GB/month — matching Proxidize — then drops to $1.60/GB mobile and $0.80/GB residential
            at 1,000 GB and above. These rates cover country-level targeting only; DataImpulse bills
            state, city, ZIP, or ASN selection at double whichever rate applies, per{" "}
            <a
              href={DATAIMPULSE_TARGETING_DOCS_URL}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="hover:text-foreground underline underline-offset-2 transition-colors"
            >
              their published targeting documentation
            </a>
            . Proxidize&rsquo;s price already includes city and ASN targeting. DataImpulse pricing
            verified {PRICING_VERIFIED_DATE};{" "}
            <a
              href={meta.dataimpulseUrl}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="hover:text-foreground underline underline-offset-2 transition-colors"
            >
              check their live pricing
            </a>
            .
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <span className="cta-glow-btn-wrap">
              <a
                href={REGISTER_URL}
                onClick={() =>
                  posthog?.capture("alternatives_cta_clicked", {
                    competitor: "dataimpulse",
                    location: "calculator",
                    product,
                    gb: activeGb,
                  })
                }
                className="cta-glow-btn-inner"
              >
                Try Proxidize NOW
                <ArrowRight size={16} className="ml-2" aria-hidden="true" />
              </a>
            </span>
            <Link
              href={meta.pricingRoute}
              className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-sm font-medium transition-colors"
            >
              Full Proxidize {product} pricing
              <ArrowUpRight size={14} aria-hidden="true" />
            </Link>
          </div>
        </MotionDiv>
      </div>
    </section>
  );
}
